import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

// Calendar overview matrix (presentational). rows = franchises, columns = a 5-year sliding window,
// cell = active bucket count as a button. Collapsible "Browse Franchise Calendars (N)" section;
// the parent owns the expanded state (so it can collapse after a cell click).
//
// Contract:
//   <SeasonSequenceMatrix
//     calendars={[{ com_code, trackingyear, active_bucket_count }]}
//     comcodes={[{ comcode, franchise_name }]}
//     selectedComCode={string|null}
//     selectedTrackingYear={number|string|""}
//     onSelectCell={(comCode, trackingYear) => void}
//     expanded={boolean}
//     onToggleExpanded={(event, isExpanded) => void}
//   />
//
// Decisions:
//   * Year universe: 2016 (floor; 2015 outlier omitted) .. max(trackingyear) in `calendars`.
//   * Window: 5 columns, default the latest year + 4 prior. Earlier/Later shift by one year;
//     Earlier disabled at 2016, Later disabled at the max year. Span <= 5 -> show all, both disabled.
//   * Selected year stays visible: a picker selection outside the window shifts the window to include
//     it (functional update, so it never fights a manual Earlier/Later).
//   * Rows: franchises (from `comcodes`) with >= 1 active calendar IN THE VISIBLE WINDOW -- no
//     all-"—" rows. The row set changes as the window shifts. Sorted by friendly name, com_code
//     tiebreaker/fallback.
//   * Missing (com_code, year) pair in-window: an em dash, not a button.
//   * Standard = 5 buckets (S/S/F/W + Other) -> neutral; 6+ -> subtle non-standard indicator.

const YEAR_FLOOR = 2016;
const NONSTANDARD_MIN = 6;
const WINDOW_SIZE = 5;

export default function SeasonSequenceMatrix({
  calendars,
  comcodes,
  selectedComCode,
  selectedTrackingYear,
  onSelectCell,
  expanded,
  onToggleExpanded,
}) {
  const rowsData = calendars || [];
  const franchiseOptions = comcodes || [];

  const nameByComCode = new Map(
    franchiseOptions.map((f) => [f.comcode, f.franchise_name || f.comcode])
  );
  const friendlyName = (comCode) => nameByComCode.get(comCode) || comCode;

  const inRange = rowsData.filter((c) => c.trackingyear >= YEAR_FLOOR);
  const supportedMin = YEAR_FLOOR;
  const supportedMax = inRange.length ? Math.max(...inRange.map((c) => c.trackingyear)) : null;
  const windowable = supportedMax != null && supportedMax - supportedMin + 1 > WINDOW_SIZE;

  // Clamp a proposed window-end year so the 5-column window stays within [supportedMin, supportedMax].
  const clampEnd = (end) => Math.min(supportedMax, Math.max(supportedMin + WINDOW_SIZE - 1, end));

  const [windowEnd, setWindowEnd] = useState(null);

  // Default the window to the latest year once data arrives.
  useEffect(() => {
    if (windowEnd == null && supportedMax != null) setWindowEnd(supportedMax);
  }, [supportedMax, windowEnd]);

  // Keep the selected year visible: a picker selection outside the window shifts the window to
  // include it. Functional update reads the current end, so it never fights a manual Earlier/Later.
  const selYear = selectedTrackingYear === "" || selectedTrackingYear == null
    ? null : Number(selectedTrackingYear);
  useEffect(() => {
    if (!windowable || selYear == null || supportedMax == null) return;
    if (selYear < supportedMin || selYear > supportedMax) return;
    setWindowEnd((prev) => {
      const end = clampEnd(prev ?? supportedMax);
      const start = end - (WINDOW_SIZE - 1);
      if (selYear > end) return clampEnd(selYear);
      if (selYear < start) return clampEnd(selYear + WINDOW_SIZE - 1);
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selYear, supportedMax]);

  // Visible year columns.
  const years = [];
  if (supportedMax != null) {
    if (windowable) {
      const end = clampEnd(windowEnd ?? supportedMax);
      for (let y = end - (WINDOW_SIZE - 1); y <= end; y++) years.push(y);
    } else {
      for (let y = supportedMin; y <= supportedMax; y++) years.push(y);
    }
  }

  const countByKey = new Map();
  for (const c of inRange) {
    countByKey.set(`${c.com_code}|${c.trackingyear}`, c.active_bucket_count);
  }

  // Rows = franchises with >= 1 active calendar in the VISIBLE window (no all-"—" rows).
  const rows = [...new Set(franchiseOptions.map((f) => f.comcode).filter(Boolean))]
    .filter((cc) => years.some((y) => countByKey.has(`${cc}|${y}`)))
    .sort((a, b) => {
      const byName = friendlyName(a).localeCompare(friendlyName(b), undefined, { sensitivity: "base" });
      return byName !== 0 ? byName : a.localeCompare(b);
    });

  const effectiveEnd = windowable ? clampEnd(windowEnd ?? supportedMax) : supportedMax;
  const earlierDisabled = !windowable || effectiveEnd - (WINDOW_SIZE - 1) <= supportedMin;
  const laterDisabled = !windowable || effectiveEnd >= supportedMax;
  const shiftEarlier = () => setWindowEnd((prev) => clampEnd((prev ?? supportedMax) - 1));
  const shiftLater = () => setWindowEnd((prev) => clampEnd((prev ?? supportedMax) + 1));

  const selectedYearNum = selYear;

  const stickyFirstCol = {
    position: "sticky",
    left: 0,
    bgcolor: "background.paper",
    zIndex: 1,
    whiteSpace: "nowrap",
  };

  const yearHeaderCells = years.map((y) => (
    <TableCell key={y} align="center" sx={{ fontWeight: 600 }}>{y}</TableCell>
  ));

  const renderRow = (comCode) => {
    const name = friendlyName(comCode);
    const cells = years.map((y) => {
      const count = countByKey.get(`${comCode}|${y}`);
      if (count == null) {
        return (
          <TableCell key={y} align="center" sx={{ color: "text.disabled" }} aria-label="No calendar">
            —
          </TableCell>
        );
      }
      const nonStandard = count >= NONSTANDARD_MIN;
      const isSelected = comCode === selectedComCode && selectedYearNum === y;
      return (
        <TableCell key={y} align="center" padding="none">
          <Button
            size="small"
            variant={isSelected ? "contained" : "text"}
            onClick={() => onSelectCell(comCode, y)}
            aria-label={`${name} ${y}: ${count} active buckets${nonStandard ? " (non-standard)" : ""}`}
            aria-pressed={isSelected}
            sx={{ minWidth: 44 }}
          >
            {count}
            {nonStandard && (
              <Box
                component="span"
                aria-hidden
                title="Non-standard (6+ buckets)"
                sx={{ ml: 0.25, fontSize: "0.6em", lineHeight: 1, verticalAlign: "super", color: "warning.main" }}
              >
                ●
              </Box>
            )}
          </Button>
        </TableCell>
      );
    });

    return (
      <TableRow key={comCode} hover>
        <TableCell sx={stickyFirstCol} title={comCode}>{name}</TableCell>
        {cells}
      </TableRow>
    );
  };

  const rowEls = rows.map(renderRow);

  let body;
  if (supportedMax == null) {
    body = (
      <Typography variant="body2" color="text.secondary">No franchise calendars to display.</Typography>
    );
  } else {
    body = (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Button size="small" variant="outlined" onClick={shiftEarlier} disabled={earlierDisabled}>Earlier</Button>
          <Button size="small" variant="outlined" onClick={shiftLater} disabled={laterDisabled}>Later</Button>
          {years.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {years[0]}–{years[years.length - 1]}
            </Typography>
          )}
        </Box>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No franchise calendars in these years. Use Earlier / Later to browse other years.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 200 + years.length * 60 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...stickyFirstCol, zIndex: 2, fontWeight: 600 }}>Franchise</TableCell>
                  {yearHeaderCells}
                </TableRow>
              </TableHead>
              <TableBody>{rowEls}</TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    );
  }

  return (
    <Accordion expanded={expanded} onChange={onToggleExpanded} sx={{ mb: 3 }}>
      <AccordionSummary expandIcon={<Typography aria-hidden>▾</Typography>}>
        <Typography variant="body2">Browse Franchise Calendars ({rows.length})</Typography>
      </AccordionSummary>
      <AccordionDetails>{body}</AccordionDetails>
    </Accordion>
  );
}
